import { get, omitBy, pick } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
  ButtonToolbar,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { MusicScheduleServices } from "../../../core/services/MusicScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { SongFields, SongListParams } from "../Songs/SongDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./MusicSchedules.css";
import SongFilters from "../Songs/SongFilters";
import {
  displayDayDateField,
  isEmptyVal,
  isNotEmpty,
  displayField,
  genreTitle,
  displayDateField,
} from "../../../utils/helpers";
import DescriptionCell from "../../components/Elements/DescriptionCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import useDecryptedParams from "../../../navigation/useDecryptParams";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { SongServices } from "../../../core/services/SongServices";
import SingerCell from "../../components/Elements/SingerCell";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import AddSongsToMusicScheduleBulkAction from "./AddSongsToMusicScheduleBulkAction";
import { setSearchString } from "../../../redux/user";
import ChartFiltersForMusicSchedule from "../Charts/ChartFiltersForMusicSchedule";
import AudioPlayer from "../../components/Elements/AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";

function AddSongsToMusicSchedule() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SongFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [licenseIds, setLicenseIds] = useState<string>("");
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [showChartFilter, setShowChartFilter] = useState(true);
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();

  const handleShowChartToggle = (showChart: boolean) => {
    setShowChartFilter(showChart);
    getSongsForMusicSchedule(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
        params_chart: showChart,
      })
    );
  };
  const params: any = useParams();
  let [decryptedParams] = useDecryptedParams(params);
  const [musicScheduleLoader, setMusicScheduleLoader] = useState(false);
  const [musicScheduleData, setMusicScheduleData] = useState<any>({});
  const songLibParams = [
    "language",
    "language_id",
    "mood",
    "mood_id",
    "name",
    "singer",
    "singer_id",
    "singer_gender",
    "singer_combination",
    "actor",
    "actor_id",
    "lyricist",
    "lyricist_id",
    "composer",
    "composer_id",
    "label",
    "label_id",
    "genre",
    "genre_id",
    "sub_genre",
    "sub_genre_id",
    "license",
    "license_id",
    "song_type",
    "media_type",
    "sourced_from",
    "media_category",
    "tempo",
    "tempo_id",
    "era",
    "era_id",
    "album",
    "festive_season",
    "festive_season_id",
    "release_year",
    "status",
    "smart_search",
    "exclude_music_time_slot",
    "exclude_music_day",
    "sort_by",
    "sort_order",
    "page",
    "rows",
    "exclude_songs_from_schedule",
  ];

  const chartParams = [
    "chart",
    "status",
    "smart_search",
    "license_id",
    "exclude_music_time_slot",
    "exclude_music_day",
    "sort_by",
    "sort_order",
    "page",
    "rows",
    "exclude_songs_from_schedule",
  ];

  const initSearchFormData = {
    chart: "",
    language: "",
    language_id: "",
    mood: "",
    mood_id: "",
    name: "",
    singer: "",
    singer_id: "",
    singer_gender: "",
    singer_combination: "",
    actor: "",
    actor_id: "",
    lyricist: "",
    lyricist_id: "",
    composer: "",
    composer_id: "",
    label: "",
    label_id: "",
    genre: "",
    genre_id: "",
    sub_genre: "",
    sub_genre_id: "",
    license: "",
    license_id: licenseIds,
    song_type: "",
    media_type: "",
    sourced_from: "",
    media_category: "",
    tempo: "",
    tempo_id: "",
    era: "",
    era_id: "",
    album: "",
    festive_season: "",
    festive_season_id: "",
    release_year: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getMusicScheduleId() {
    return decryptedParams.id ? decryptedParams.id.toString() : null;
  }
  async function getMusicScheduleDetail(id: number) {
    try {
      setMusicScheduleLoader(true);
      const res = await MusicScheduleServices.viewMusicSchedule(id);
      const respData = get("data.data", res);
      setMusicScheduleData({ ...respData });
      setLicenseIds(respData?.company?.active_contract?.music_licenses);
      getSongsForMusicSchedule(
        getSearchParams({
          license_id: respData?.company?.active_contract?.music_licenses,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
          params_chart: true,
        })
      );
      setMusicScheduleLoader(false);
    } catch (error) {
      setMusicScheduleLoader(false);
    }
  }
  async function getSongsForMusicSchedule(params: SongListParams) {
    try {
      if (params?.params_chart) {
        setData([]);
        setTotal(0);
        if (isEmptyVal(params?.chart)) return;
      }
      setLoading(true);
      const res = await SongServices.getSongs(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(ContentRoutes.MUSIC_SCHEDULES);
    }
  }

  async function bulkAddAction() {
    try {
      let params = {
        song_ids: checkedKeys.join(),
        time_slot_id: selectedSlot,
        play_for_day: selectedDay,
      };
      let res = await MusicScheduleServices.addSongsToMusicSchedule(
        musicScheduleData.id,
        params
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
      getSongsForMusicSchedule(
        getSearchParams({
          smart_search: smartSearchInput,
        })
      );
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      getSongsForMusicSchedule(
        getSearchParams({
          smart_search: smartSearchInput,
        })
      );
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function init() {
    try {
      getMusicScheduleId() && getMusicScheduleDetail(getMusicScheduleId());
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, [decryptedParams.id]);

  function getSearchParams(data?: any) {
    let paramsChart: boolean = showChartFilter;
    if (data.params_chart !== null && data.params_chart !== undefined)
      paramsChart = data.params_chart;
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ exclude_music_time_slot: selectedSlot },
      ...{ exclude_music_day: selectedDay },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SongListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (getMusicScheduleId()) {
      params.exclude_songs_from_schedule = getMusicScheduleId();
    }
    if (isEmptyVal(params.license_id) && licenseIds !== "") {
      params.license_id = licenseIds;
    }

    const selectedParams = pick(
      paramsChart ? chartParams : songLibParams,
      params
    );
    selectedParams.params_chart = paramsChart;
    return selectedParams;
  }

  function onPageChange(page: number) {
    setPage(page);
    getSongsForMusicSchedule(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getSongsForMusicSchedule(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    formData.params_chart = false;
    getSongsForMusicSchedule(getSearchParams(formData));
  }

  function submitChartSearch(formData: any) {
    setFormData(formData);
    formData.params_chart = true;
    getSongsForMusicSchedule(getSearchParams(formData));
  }

  function submitExcludedSearch(params: any) {
    getSongsForMusicSchedule(getSearchParams(params));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongsForMusicSchedule(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterSongs = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getSongsForMusicSchedule(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongsForMusicSchedule(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getSongsForMusicSchedule(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };
  return (
    <div className="add-songs-to-music-schedule">
      {musicScheduleLoader && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content={t("LOADING")}
          center
          backdrop={true}
        />
      )}
      {getMusicScheduleId() > 0 ? (
        <div className="followups">
          <Container className="container">
            <Header className="header mb-2 add-songs-music-summary">
              <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item colspan={16}>
                  <h3>
                    <span className="add-songs-music-summary-label">
                      {t("MUSIC_SCHEDULE")} {t("NAME")}
                    </span>
                    <span>{musicScheduleData?.playlist_title}</span>
                  </h3>
                  <h4>
                    <span>
                      <b>
                        {t("IS")} {t("DEFAULT")}
                      </b>
                      :{" "}
                      {musicScheduleData?.is_default_playlist
                        ? t("YES")
                        : t("NO")}
                    </span>
                    <span>
                      <b>
                        {t("STORE")} {t("GROUP")}
                      </b>
                      : {musicScheduleData?.store_group?.group_name}
                    </span>
                    {!isEmptyVal(musicScheduleData?.play_start_date) && (
                      <span>
                        <b>{t("DATE")}</b>:{" "}
                        {!isEmptyVal(musicScheduleData?.play_start_date)
                          ? t("FROM") +
                            " " +
                            displayDateField(
                              musicScheduleData?.play_start_date
                            ) +
                            " " +
                            t("TO") +
                            " " +
                            displayDateField(musicScheduleData?.play_end_date)
                          : null}
                      </span>
                    )}
                  </h4>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Header>
            <Content>
              {licenseIds !== "" && (
                <div className="row">
                  <div className="row mb-2">
                    <ButtonToolbar className="mb-hide">
                      <Button
                        className={`add-music-schedule-source-tab ${
                          showChartFilter ? "active" : ""
                        }`}
                        onClick={() => handleShowChartToggle(true)}
                      >
                        {t("SEARCH")} {t("SONG")} {t("FROM")} {t("CHART")}
                      </Button>
                      {UtilityServices.getLoggedInUser("user_type") ==
                        roleTypeArr.backend && (
                        <Button
                          className={`add-music-schedule-source-tab ${
                            !showChartFilter ? "active" : ""
                          }`}
                          onClick={() => handleShowChartToggle(false)}
                        >
                          {t("SEARCH")} {t("SONG")} {t("FROM")} {t("LIBRARY")}
                        </Button>
                      )}
                    </ButtonToolbar>
                  </div>
                  <ChartFiltersForMusicSchedule
                    onSubmit={submitChartSearch}
                    onClear={clearSearch}
                    musicScheduleData={musicScheduleData}
                    showOutChartFilters={showChartFilter}
                    showHeader={false}
                  />
                  {UtilityServices.getLoggedInUser("user_type") ==
                    roleTypeArr.backend && (
                    <SongFilters
                      onSubmit={submitSearch}
                      onClear={clearSearch}
                      onlyLicenses={licenseIds}
                      showOutMusicFilters={!showChartFilter}
                      showHeader={false}
                    />
                  )}
                </div>
              )}

              <div className="add-music-schedule-actions-row">
                <AddSongsToMusicScheduleBulkAction
                  musicScheduleData={musicScheduleData}
                  checkedKeys={checkedKeys}
                  onAddSongs={bulkAddAction}
                  onDeleteSongs={bulkDeleteAction}
                  selectedSlot={selectedSlot}
                  setSelectedSlot={setSelectedSlot}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  onSearchSongs={submitExcludedSearch}
                />
                <div className="add-music-schedule-search">
                  <InputGroup className="add-music-schedule-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity: t("TITLE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterSongs(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterSongs(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div
                className={
                  total > 0 ? "usr-tbl-border mt-2" : "no-record-table mt-2"
                }
              >
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  hover={true}
                  renderEmpty={() => (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <h4>
                        {showChartFilter && isEmptyVal(formData.chart)
                          ? t("PLEASE_SELECT_FIELD", {
                              field: t("CHART"),
                            })
                          : t("NO_ENTITY_FOUND_FOR", {
                              entity: t("SONG"),
                              for: musicScheduleData?.playlist_title,
                            })}
                      </h4>
                    </div>
                  )}
                >
                  <Column width={40} align="center" fixed>
                    <CustomHeaderCell>
                      <div>
                        <Checkbox
                          inline
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={handleCheckAll}
                        />
                      </div>
                    </CustomHeaderCell>
                    <CheckCell
                      dataKey="id"
                      checkedKeys={checkedKeys}
                      onChange={handleCheck}
                    />
                  </Column>
                  <Column width={60} align="center" fixed>
                    <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                    <MediaPlayerFormatter
                      rowData={(rowData: any) => rowData}
                      mediaUrlKey={"media_url"}
                      mediaImageKey={"media_image"}
                    />
                  </Column>
                  <Column width={200} sortable resizable flexGrow={1}>
                    <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                    <CustomCell dataKey="title">
                      {(rowData: any) => (
                        <span title={`${rowData.title}`}>
                          {displayField(rowData.title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={160}>
                    <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                    <SingerCell rowData={(rowData: any) => rowData} />
                  </Column>
                  <Column width={160} sortable resizable>
                    <CustomHeaderCell>{t("GENRE")}</CustomHeaderCell>
                    <CustomCell dataKey="genre_title">
                      {(rowData: any) => (
                        <span title={genreTitle(rowData)}>
                          {displayField(rowData.genre_title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={140} resizable>
                    <CustomHeaderCell>{t("LANGUAGE")}</CustomHeaderCell>
                    <CustomCell dataKey="language_title">
                      {(rowData: any) => (
                        <span title={`${rowData.language_title}`}>
                          {displayField(rowData.language_title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={80}>
                    <CustomHeaderCell>{t("MORE")}</CustomHeaderCell>
                    <SongInfoCell
                      rowData={(rowData: any) => rowData}
                      showData={[
                        "era",
                        "mood",
                        "license",
                        "lyricist",
                        "composer",
                        "tempo",
                      ]}
                    />
                  </Column>
                </Table>
              </div>
            </Content>
            <Footer className="footer">
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="xs"
                layout={["total", "-", "limit", "|", "pager", "skip"]}
                total={total}
                limitOptions={appConstObj.limit_options}
                limit={rows}
                activePage={page}
                onChangePage={onPageChange}
                onChangeLimit={handleChangeRows}
              />
            </Footer>
            <AudioPlayer />
            <ToastContainer />
          </Container>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export default AddSongsToMusicSchedule;
